"use client";

import { useState } from "react";

import { cn } from "@/lib/utils";

import { getIconUrls } from "./asset-icon-urls";

const BADGE_TONES = 6;

/** A stable palette slot for a symbol or name. */
function badgeColor(seed: string): string {
  const hash = seed.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return `hsl(var(--badge-${(Math.abs(hash) % BADGE_TONES) + 1}))`;
}

/**
 * The letter placeholder used wherever an icon is missing: the Fexr wallet's
 * InitialsBadge (lib/core/widgets/initials_badge.dart) in CSS. A glossy disc
 * lit from the top (the colour 18% lighter → 12% darker), a soft drop shadow,
 * an edge vignette, a bevelled rim, and bold white letters at 36% of the
 * size. [color] overrides the palette, e.g. a vault's brand colour.
 */
export function InitialsBadge({
  text,
  seed,
  size,
  color,
  className,
}: {
  text: string;
  /** What picks the palette colour; defaults to [text]. */
  seed?: string;
  size: number;
  color?: string;
  className?: string;
}) {
  const c = color || badgeColor(seed ?? text);
  const dark = `color-mix(in srgb, ${c} 65%, black)`;
  const rim = Math.max(1, size * 0.045);
  return (
    <span
      aria-hidden="true"
      className={cn(
        "relative inline-flex shrink-0 select-none items-center justify-center rounded-full",
        className,
      )}
      style={{
        width: size,
        height: size,
        background: `linear-gradient(to bottom, color-mix(in srgb, ${c} 82%, white), color-mix(in srgb, ${c} 88%, black))`,
        boxShadow: `0 ${size * 0.04}px ${size * 0.1}px color-mix(in srgb, ${dark} 28%, transparent)`,
      }}
    >
      {/* Edge vignette, then the bevelled rim: light on top, shade below. */}
      <span
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, transparent 82%, hsl(0 0% 0% / 0.14))",
        }}
      />
      <span
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          background:
            "linear-gradient(to bottom, hsl(0 0% 100% / 0.55), transparent 40%, transparent 60%, hsl(0 0% 0% / 0.18))",
          WebkitMask: `radial-gradient(closest-side, transparent calc(100% - ${rim}px), #000 calc(100% - ${rim}px))`,
          mask: `radial-gradient(closest-side, transparent calc(100% - ${rim}px), #000 calc(100% - ${rim}px))`,
        }}
      />
      <span
        className="relative font-sans"
        style={{
          fontSize: size * 0.36,
          fontWeight: 800,
          letterSpacing: "-0.02em",
          lineHeight: 1,
          color: "hsl(var(--badge-ink))",
          textShadow: `0 0.5px 1px color-mix(in srgb, ${dark} 35%, transparent)`,
        }}
      >
        {text}
      </span>
    </span>
  );
}

export interface AssetIconProps {
  symbol: string;
  size?: number;
  className?: string;
  loadRemote?: boolean;
}

export function AssetIcon({
  symbol,
  size = 24,
  className,
  loadRemote = true,
}: AssetIconProps) {
  const urls = getIconUrls(symbol);
  const [idx, setIdx] = useState(0);

  if (!loadRemote || !urls.length || idx >= urls.length)
    return <Initials symbol={symbol} size={size} className={className} />;

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={urls[idx]}
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      className={cn("shrink-0 rounded-full object-cover", className)}
      style={{ width: size, height: size }}
      onError={() => setIdx((i) => i + 1)}
    />
  );
}

export interface AssetIconStackProps {
  symbols: string[];
  iconSize?: number;
  max?: number;
  className?: string;
}

export function AssetIconStack({
  symbols,
  iconSize = 20,
  max = 3,
  className,
}: AssetIconStackProps) {
  const shown = symbols.slice(0, max);
  if (!shown.length) return null;
  if (shown.length === 1)
    return (
      <AssetIcon symbol={shown[0]!} size={iconSize} className={className} />
    );

  const overlap = Math.round(iconSize * 0.45);
  const totalWidth = iconSize + (shown.length - 1) * overlap;

  return (
    <div
      aria-hidden="true"
      className={cn("relative shrink-0", className)}
      style={{ width: totalWidth, height: iconSize }}
    >
      {shown.map((sym, i) => (
        <div
          key={sym + i}
          className="absolute top-0 rounded-full ring-2 ring-white dark:ring-border"
          style={{ left: i * overlap, zIndex: i }}
        >
          <AssetIcon symbol={sym} size={iconSize} />
        </div>
      ))}
    </div>
  );
}

function Initials({
  symbol,
  size,
  className,
}: {
  symbol: string;
  size: number;
  className?: string;
}) {
  return (
    <InitialsBadge
      text={symbol.slice(0, 2).toUpperCase()}
      seed={symbol}
      size={size}
      className={className}
    />
  );
}
